
import React, { useState, useEffect } from 'react';
import { Separator } from '@/components/ui/separator';
import { Author } from '@/utils/storiesData';
import { extractImageUrlsFromMarkdown, validateImageUrl } from '@/utils/imageUtils';
import ContentRenderer from './ContentRenderer';
import TagList from './TagList';
import ImageModal from './ImageModal';
import { toast } from '@/components/ui/use-toast';
import { useIsMobile } from '@/hooks/use-mobile';
import HorizontalScroller from '@/components/mobile/HorizontalScroller';
import StoryCard from '@/components/story/StoryCard';
import { Story } from '@/utils/storiesData';
import YouTubeSubscribeBanner from './YouTubeSubscribeBanner';

interface ArticleContentProps {
  subtitle: string;
  content: string;
  tags: string[] | {
    name: string;
  }[] | any[];
  author: Author;
  relatedStories?: Story[];
}

const ArticleContent: React.FC<ArticleContentProps> = ({
  subtitle,
  content,
  tags = [],
  author,
  relatedStories = []
}) => {
  const [imageOpen, setImageOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState<number>(0);
  const [contentImageUrls, setContentImageUrls] = useState<string[]>([]);
  const isMobile = useIsMobile();
  
  useEffect(() => {
    if (!content) {
      console.warn('ArticleContent - No content provided');
      return;
    }
    try {
      const extractedUrls = extractImageUrlsFromMarkdown(content);
      const validUrls = extractedUrls.map(url => validateImageUrl(url)).filter(url => url !== '');
      setContentImageUrls(validUrls);
      if (validUrls.length === 0 && content.includes('![')) {
        console.warn('ArticleContent - Found image markdown syntax but no valid URLs were extracted');
      }
    } catch (error) {
      console.error('ArticleContent - Error extracting image URLs:', error);
      toast({
        title: "Error loading images",
        description: "There was a problem loading images from the article content",
        variant: "destructive"
      });
    }
  }, [content]);
  
  const handleImageClick = (index: number) => {
    if (contentImageUrls.length === 0) {
      console.warn('No images available to display');
      return;
    }
    const safeIndex = Math.max(0, Math.min(index, contentImageUrls.length - 1));
    setCurrentImageIndex(safeIndex);
    setImageOpen(true);
  };
  
  const handlePrevImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentImageIndex(prev => prev === 0 ? contentImageUrls.length - 1 : prev - 1);
  };
  
  const handleNextImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentImageIndex(prev => prev === contentImageUrls.length - 1 ? 0 : prev + 1);
  };
  
  console.log('ArticleContent - Tags received:', tags);
  
  return (
    <div className="prose-container max-w-full w-full overflow-hidden">
      <p className="text-xl text-gray-300 mb-6 mt-2 font-gt-super leading-article">
        {subtitle}
      </p>

      <ContentRenderer 
        content={content} 
        contentImageUrls={contentImageUrls} 
        handleImageClick={handleImageClick} 
      />

      {/* Display tags */}
      <TagList tags={tags} />

      <ImageModal 
        open={imageOpen} 
        onOpenChange={setImageOpen} 
        images={contentImageUrls} 
        currentIndex={currentImageIndex} 
        onPrevImage={handlePrevImage} 
        onNextImage={handleNextImage} 
      />

      <Separator className="my-8" />
      
      {relatedStories && relatedStories.length > 0 && (
        <div className="mt-6 mb-8 w-full max-w-full overflow-hidden">
          <h3 className="font-medium uppercase tracking-wider mb-4 text-base text-foreground">Read more</h3>
          
          {isMobile ? (
            <HorizontalScroller noLeftPadding showNextCard={true} snap>
              {relatedStories.map(story => (
                <StoryCard 
                  key={story.id} 
                  story={story} 
                  featured={false} 
                  aspectRatio="3/2" 
                  hideAuthor={true} 
                />
              ))}
            </HorizontalScroller>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 w-full">
              {relatedStories.map(story => (
                <StoryCard 
                  key={story.id} 
                  story={story} 
                  featured={false} 
                  aspectRatio="3/2" 
                  hideAuthor={true} 
                />
              ))}
            </div>
          )}
        </div>
      )}
      
      {/* Only show on desktop */}
      <div className="hidden md:block">
        <YouTubeSubscribeBanner />
      </div>
    </div>
  );
};

export default ArticleContent;
